import { ApiError, apiFetch } from '@/lib/api/client';
const KNOWN_ERROR_CODES = new Set([
    'ai_disabled',
    'ai_not_configured',
    'ai_not_enabled',
    'ai_upstream_error',
]);
/** GET /ai/availability: whether AI assistance may be used on this study. */
export async function getAiAvailability(studyId) {
    const data = await apiFetch(`/ai/availability?studyId=${encodeURIComponent(studyId)}`);
    const reason = data.reason && KNOWN_ERROR_CODES.has(data.reason) ? data.reason : null;
    return { available: data.available, reason };
}
/**
 * A failed `/ai/complete` call. `code` is the server's reason when it is one
 * the UI knows how to explain, else null (network failure, a plain-text body,
 * an unknown code). The server's own `message` stays on `.message` for logs
 * only: callers map `code` to their own copy and must never show the raw text.
 */
export class AiAssistError extends ApiError {
    code;
    constructor(status, message, code) {
        super(status, message);
        this.code = code;
        this.name = 'AiAssistError';
    }
}
/** The known error code in a `/ai/complete` failure body, or null. */
export function parseAiAssistErrorCode(body) {
    try {
        const parsed = JSON.parse(body);
        const code = parsed?.error;
        return typeof code === 'string' && KNOWN_ERROR_CODES.has(code)
            ? code
            : null;
    }
    catch {
        return null;
    }
}
/** The known error code on a thrown `askClaude` failure, or null for anything else. */
export function getAiAssistErrorCode(error) {
    return error instanceof AiAssistError ? error.code : null;
}
async function callClaude(systemPrompt, content, options) {
    const contentBlocks = typeof content === 'string' ? [{ type: 'text', text: content }] : content;
    let data;
    try {
        data = await apiFetch('/ai/complete', {
            method: 'POST',
            body: JSON.stringify({
                systemPrompt,
                content: contentBlocks,
                maxTokens: options.maxTokens ?? 1024,
                studyId: options.studyId,
            }),
        });
    }
    catch (error) {
        // apiFetch keeps the raw response text as the message; lift the error
        // code out of it here so every caller sees one typed failure.
        if (error instanceof ApiError) {
            throw new AiAssistError(error.status, error.message, parseAiAssistErrorCode(error.message));
        }
        throw error;
    }
    return data.text;
}
export async function askClaude(systemPrompt, userMessage, options) {
    return callClaude(systemPrompt, userMessage, options);
}
export async function askClaudeWithImages(systemPrompt, content, options) {
    return callClaude(systemPrompt, content, options);
}
